import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createAdminProblem,
  deleteAdminProblem,
  listAdminProblems,
  updateAdminProblem,
  type AdminProblem,
  type CreateAdminProblemPayload,
  type ProblemDifficulty,
} from '@/services/adminProblemsApi';
import { getUserFriendlyMessage, parseApiError } from '@/services/apiError';
import FormMessage from '@/components/common/FormMessage';
import ContentApiErrorState from '@/components/common/ContentErrorState';

interface ProblemFormState {
  title: string;
  description: string;
  difficulty: ProblemDifficulty;
  category: string;
  tags: string;
  diagnostic: boolean;
}

const INITIAL_FORM: ProblemFormState = {
  title: '',
  description: '',
  difficulty: 'EASY',
  category: '',
  tags: '',
  diagnostic: false,
};

const DIFFICULTY_LABEL: Record<ProblemDifficulty, string> = {
  EASY: '简单',
  MEDIUM: '中等',
  HARD: '困难',
};

export default function AdminProblemsPage() {
  const [problems, setProblems] = useState<AdminProblem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const [formState, setFormState] = useState<ProblemFormState>(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    void fetchProblems();
  }, []);

  const editingProblem = useMemo(() => {
    if (!editingId) {
      return undefined;
    }
    return problems.find((item) => item.id === editingId);
  }, [editingId, problems]);

  const fetchProblems = async () => {
    setLoading(true);
    setListError(null);
    try {
      const items = await listAdminProblems();
      setProblems(items);
    } catch (unknownError) {
      setListError(unknownError);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormState(INITIAL_FORM);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    const payload = toPayload(formState);

    try {
      if (editingId) {
        await updateAdminProblem(editingId, payload);
        setSuccess('题目已更新');
      } else {
        await createAdminProblem(payload);
        setSuccess('题目已创建');
      }
      await fetchProblems();
      resetForm();
    } catch (unknownError) {
      setError(toAdminProblemsErrorMessage(unknownError, '保存题目失败'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (problem: AdminProblem) => {
    setEditingId(problem.id);
    setFormState({
      title: problem.title,
      description: problem.description,
      difficulty: problem.difficulty,
      category: problem.category,
      tags: problem.tags.join(', '),
      diagnostic: problem.diagnostic,
    });
    setError('');
    setSuccess('');
  };

  const handleDelete = async (problemId: string) => {
    const accepted = window.confirm('确认删除该题目吗？删除后无法恢复。');
    if (!accepted) {
      return;
    }

    setError('');
    setSuccess('');
    setDeletingId(problemId);

    try {
      await deleteAdminProblem(problemId);
      setSuccess('题目已删除');
      if (editingId === problemId) {
        resetForm();
      }
      await fetchProblems();
    } catch (unknownError) {
      setError(toAdminProblemsErrorMessage(unknownError, '删除题目失败'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className='max-w-[1080px] mx-auto px-6 py-12'>
      <div className='flex items-end justify-between gap-4 mb-8'>
        <div>
          <h1
            className='font-semibold text-apple-near-black mb-2'
            style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            题目管理
          </h1>
          <p className='text-apple-gray text-[0.9375rem]'>仅管理员可用：创建、编辑、删除题目。</p>
        </div>
        <button type='button' className='btn btn-secondary' onClick={resetForm}>
          新建题目
        </button>
      </div>

      {(error || success) && (
        <div className='space-y-3 mb-6'>
          {error ? <FormMessage tone='error' message={error} /> : null}
          {success ? <FormMessage tone='success' message={success} /> : null}
        </div>
      )}

      <div className='grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6'>
        <form className='bg-white rounded-2xl p-6 shadow-card-sm space-y-4 h-fit' onSubmit={handleSubmit}>
          <h2 className='font-semibold text-apple-near-black text-[1.125rem]'>{editingId ? '编辑题目' : '新建题目'}</h2>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='problem-title'>
              标题
            </label>
            <input
              id='problem-title'
              className='input'
              required
              disabled={saving}
              value={formState.title}
              onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
            />
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='problem-description'>
              描述
            </label>
            <textarea
              id='problem-description'
              className='input min-h-28'
              required
              disabled={saving}
              value={formState.description}
              onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div className='space-y-1.5'>
              <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='problem-difficulty'>
                难度
              </label>
              <select
                id='problem-difficulty'
                className='input'
                disabled={saving}
                value={formState.difficulty}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, difficulty: event.target.value as ProblemDifficulty }))
                }
              >
                <option value='EASY'>简单</option>
                <option value='MEDIUM'>中等</option>
                <option value='HARD'>困难</option>
              </select>
            </div>

            <div className='space-y-1.5'>
              <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='problem-category'>
                分类
              </label>
              <input
                id='problem-category'
                className='input'
                required
                disabled={saving}
                value={formState.category}
                onChange={(event) => setFormState((prev) => ({ ...prev, category: event.target.value }))}
              />
            </div>
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='problem-tags'>
              标签（逗号分隔）
            </label>
            <input
              id='problem-tags'
              className='input'
              placeholder='array, string'
              disabled={saving}
              value={formState.tags}
              onChange={(event) => setFormState((prev) => ({ ...prev, tags: event.target.value }))}
            />
          </div>

          <label className='flex items-center gap-2 text-[0.875rem] text-apple-near-black'>
            <input
              type='checkbox'
              checked={formState.diagnostic}
              disabled={saving}
              onChange={(event) => setFormState((prev) => ({ ...prev, diagnostic: event.target.checked }))}
            />
            诊断题
          </label>

          <div className='flex gap-3 pt-2'>
            <button type='submit' className='btn btn-primary' disabled={saving}>
              {saving ? '保存中…' : editingId ? '保存修改' : '创建题目'}
            </button>
            {editingId ? (
              <button type='button' className='btn btn-secondary' onClick={resetForm}>
                取消编辑
              </button>
            ) : null}
          </div>
        </form>

        <section className='bg-white rounded-2xl p-6 shadow-card-sm'>
          <h2 className='font-semibold text-apple-near-black text-[1.125rem] mb-5'>题目列表</h2>

          {loading ? <p className='text-apple-gray text-[0.9375rem]'>加载中…</p> : null}

          {!loading && listError ? (
            <div className='mb-4'>
              <ContentApiErrorState error={listError} onRetry={() => void fetchProblems()} />
            </div>
          ) : null}

          {!loading && !listError && problems.length === 0 ? <p className='text-apple-gray text-[0.9375rem]'>暂无题目</p> : null}

          <div className='space-y-3'>
            {!listError &&
              problems.map((problem) => (
              <article key={problem.id} className='border border-apple-border rounded-xl px-4 py-4'>
                <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2'>
                  <div>
                    <h3 className='text-apple-near-black font-medium text-[0.95rem]'>{problem.title}</h3>
                    <p className='text-apple-gray text-[0.8125rem] mt-1'>
                      {DIFFICULTY_LABEL[problem.difficulty]} · {problem.category}
                    </p>
                    {problem.tags.length > 0 ? (
                      <p className='text-apple-gray text-[0.8125rem] mt-1'>标签：{problem.tags.join('、')}</p>
                    ) : null}
                  </div>
                  <div className='flex gap-2'>
                    <button type='button' className='btn btn-secondary' onClick={() => startEdit(problem)}>
                      编辑
                    </button>
                    <button
                      type='button'
                      className='btn btn-danger'
                      onClick={() => void handleDelete(problem.id)}
                      disabled={deletingId === problem.id}
                    >
                      {deletingId === problem.id ? '删除中…' : '删除'}
                    </button>
                  </div>
                </div>
              </article>
              ))}
          </div>
        </section>
      </div>

      {editingProblem ? (
        <p className='text-apple-gray text-[0.8125rem] mt-4'>
          当前正在编辑：{editingProblem.title}（ID: {editingProblem.id}）
        </p>
      ) : null}
    </div>
  );
}

function toAdminProblemsErrorMessage(error: unknown, fallback: string) {
  const parsed = parseApiError(error);

  if (parsed.statusCode === 403) {
    return '当前账号没有管理员权限，无法执行该操作。';
  }

  return getUserFriendlyMessage(error, fallback);
}

function toPayload(formState: ProblemFormState): CreateAdminProblemPayload {
  const tags = splitCsv(formState.tags);

  return {
    title: formState.title,
    description: formState.description,
    difficulty: formState.difficulty,
    category: formState.category,
    tags,
    diagnostic: formState.diagnostic,
    knowledgePoints: tags,
  };
}

function splitCsv(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
